// Client-side port of TradeMachine.Money (lib/trade_machine/money.ex).
//
// The salary fields re-format what people type, so the browser has to agree
// with the server about what "2.6m" means — otherwise the field rewrites the
// shorthand into something else before the server parser ever sees it (#488).
//
// Kept free of DOM access so it can be unit-tested with `node --test`; the hook
// in salary-input.js does the caret and event handling.
//
// Amounts stay decimal strings rather than Numbers: a fat-fingered salary can
// run past what a float holds exactly, and a display must never invent digits.

// How many places each suffix shifts the decimal point.
const MULTIPLIERS = { m: 6, k: 3 }

// Digits with at most one decimal point, no sign — the same shape
// TradeMachine.Money accepts. Rejects "", ".", "1.", "-5" and "1.2.3"; the sign
// is what keeps negatives out.
const NUMERIC = /^\d*\.?\d+$/

/**
 * Parses a typed dollar amount into whole dollars.
 *
 * Mirrors `TradeMachine.Money.parse/1` exactly — same suffix rules, same
 * half-up rounding — so what the field shows is what the server stores.
 *
 * @param {string} value raw field contents, e.g. "2.6m" or "$12,500,000"
 * @returns {string|null} whole dollars as a digit string, or null for input the
 *   server would reject too (so the caller can leave it alone).
 */
export function parseDollars(value) {
  const cleaned = String(value)
    .replace(/[\s,_$]/g, "")
    .toLowerCase()

  // A lone "m"/"k" has no number in front of it, so it stays garbage rather
  // than becoming a million dollars.
  const suffix = cleaned.slice(-1)
  const scaled = cleaned.length > 1 && Object.hasOwn(MULTIPLIERS, suffix)
  const digits = scaled ? cleaned.slice(0, -1) : cleaned

  if (!NUMERIC.test(digits)) return null

  return scaleToDollars(digits, scaled ? MULTIPLIERS[suffix] : 0)
}

/**
 * Groups a whole-dollar digit string: "2600000" → "2,600,000". Operates on the
 * string so very large amounts stay exact.
 *
 * @param {string} digits
 * @returns {string}
 */
export function groupDigits(digits) {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
}

// ── Internals ─────────────────────────────────────────────────────────

// Shifts the decimal point right by `exponent` places and rounds half-up to a
// whole dollar — `Decimal.mult |> Decimal.round(0, :half_up)` on the server —
// without ever going through a float.
function scaleToDollars(digits, exponent) {
  const [whole, fraction = ""] = digits.split(".")
  const padded = fraction.padEnd(exponent, "0")
  const shifted = whole + padded.slice(0, exponent)
  const dropped = padded.slice(exponent)
  const roundUp = dropped.charAt(0) >= "5"

  return (BigInt(shifted || "0") + (roundUp ? 1n : 0n)).toString()
}
